import type { Schemas } from '#shopware'
import type { SearchApiHit } from '~/types/searchApi'

/**
 * Maps A4G Search API hits onto the shape the shared `ProductListingCard`
 * expects: `manufacturer` (a plain string) becomes `{ name }`, relative
 * `media[].path` passes through as-is (the image proxy resolves it, same as Algolia
 * hits), and `seoUrl` becomes the `seoUrls` entry `getProductRoute`/`getProductUrl`
 * read. That entry carries `isCanonical: true`, which is not decoration: this repo
 * patches `getProductUrl` (see `patches/@shopware+helpers+1.6.0.patch`) to select
 * `seoUrls.find(u => u.isCanonical === true)` instead of upstream's `seoUrls[0]`, so
 * without the flag the helper falls through to the technical `/detail/{id}` path. The
 * Search API returns exactly one SEO URL per product and that one is the canonical,
 * so asserting it here is accurate rather than assumed.
 *
 * Price is *selected*, never computed: `net`/`gross` is picked by the customer
 * group's `displayGross`, and the struck-through reference price is driven purely by
 * `listPrice` being present — no discount-percentage arithmetic. `onSale` is NOT a
 * signal for this: since A4G-3310, the Search API computes it from Shopware's
 * `campaign` custom field (matching Algolia's existing `isSale`), decoupled from
 * whether a real `listPrice` discount exists — same as `useProductPriceCustom`'s
 * `isListPrice` for the rest of the storefront, which also only checks
 * `listPrice.price`. Only `ProductListingCard`'s actual field reads are populated;
 * this is not a full `Schemas['Product']`, hence the cast.
 */
export function mapSearchApiItems(hits: SearchApiHit[], displayGross: boolean): Schemas['Product'][] {
    return hits.map(hit => mapSearchApiItem(hit, displayGross))
}

function mapSearchApiItem(hit: SearchApiHit, displayGross: boolean): Schemas['Product'] {
    const unitPrice = displayGross ? hit.price.gross : hit.price.net
    const showListPrice = hit.listPrice != null
    const listPriceValue = showListPrice
        ? (displayGross ? hit.listPrice!.gross : hit.listPrice!.net)
        : undefined

    const calculatedPrice = {
        apiAlias: 'calculated_price',
        unitPrice,
        totalPrice: unitPrice,
        listPrice: listPriceValue != null
            ? {
                apiAlias: 'cart_list_price',
                price: listPriceValue,
                // NaN (not e.g. 1) so a stray render of the number itself (a component
                // that isn't just truthiness-gated on it) fails loudly instead of
                // showing a plausible-looking but fabricated "-1%".
                percentage: NaN,
            }
            : null,
    } as unknown as Schemas['CalculatedPrice']

    return {
        id: hit.id,
        name: hit.name,
        manufacturer: hit.manufacturer != null ? { name: hit.manufacturer } : undefined,
        media: (hit.media ?? []).map(media => ({ media: { path: media.path } })),
        seoUrls: hit.seoUrl != null ? [{ seoPathInfo: hit.seoUrl, isCanonical: true }] : [],
        calculatedPrice,
        calculatedPrices: [calculatedPrice],
    } as unknown as Schemas['Product']
}
